import React, { useEffect, useRef, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { useHistory } from "react-router-dom";
import { LOGIN_ROUTE_PATH } from "../app_utils/route_paths/resolver/PageRoutes";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { Messages } from "primereact/messages";
import { FaFacebook, FaGithub, FaGoogle, FaTwitter } from "react-icons/fa";
import { SocialLoginProvider, getSocialLoginLabel, getSocialLoginUrl } from "../app_utils/utils/SocialLoginUtils";

const Register = () => {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [countryId, setCountryId] = useState<number | null>(null);
  const [countries, setCountries] = useState<any>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isRegistered, setIsRegistered] = useState<boolean>(false);
  const history = useHistory();
  const message = useRef<any>();

  const fetchCountries = () => {
    new BaseApiServiceImpl("/api/v1/lookups/countries")
      .getRequestWithJsonResponse({ offset: 0, limit: 500 })
      .then(async (response) => {
        setCountries(response?.records);
      })
      .catch((error) => {
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const validateForm = () => {
    if (!firstName.trim()) {
      MessageUtils.showErrorMessage(message, "First name is required");
      return false;
    }
    if (!lastName.trim()) {
      MessageUtils.showErrorMessage(message, "Last name is required");
      return false;
    }
    if (!emailAddress.trim()) {
      MessageUtils.showErrorMessage(message, "Email address is required");
      return false;
    }
    if (!emailAddress.includes("@")) {
      MessageUtils.showErrorMessage(message, "Please enter a valid email address");
      return false;
    }
    if (!phoneNumber.trim()) {
      MessageUtils.showErrorMessage(message, "Phone number is required");
      return false;
    }
    if (!countryId) {
      MessageUtils.showErrorMessage(message, "Country is required");
      return false;
    }
    if (!password) {
      MessageUtils.showErrorMessage(message, "Password is required");
      return false;
    }
    if (password.length < 6) {
      MessageUtils.showErrorMessage(message, "Password must be at least 6 characters");
      return false;
    }
    if (password !== confirmPassword) {
      MessageUtils.showErrorMessage(message, "Passwords do not match");
      return false;
    }
    return true;
  };

  const submitRegistration = () => {
    if (!validateForm()) return;

    let userData: any = {
      firstName: firstName,
      lastName: lastName,
      emailAddress: emailAddress,
      phoneNumber: phoneNumber,
      password: password,
      confirmPassword: confirmPassword,
      countryId: countryId,
    };

    setIsSaving(true);
    new BaseApiServiceImpl("/api/v1/auth/register")
      .postRequestWithJsonResponse(userData)
      .then(async (response) => {
        setIsSaving(false);
        setIsRegistered(true);
        MessageUtils.showSuccessMessage(
          message,
          "Registration successful! Please check your email for the OTP to verify your account."
        );
      })
      .catch((error) => {
        setIsSaving(false);
        MessageUtils.showErrorMessage(message, error.message);
      });
  };

  const goToLogin = () => {
    history.replace(LOGIN_ROUTE_PATH);
  };

  const handleOAuthLogin = (provider: SocialLoginProvider) => {
    window.location.assign(getSocialLoginUrl(provider));
  };

  if (isRegistered) {
    return (
      <div
        className="login-page flex flex-column"
        style={{
          background: "linear-gradient(to right, #4b79a1, #283e51)",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="login-panel card shadow-lg p-4 rounded-lg"
          style={{ maxWidth: "450px", width: "100%" }}
        >
          <div className="text-center mb-4">
            <i className="pi pi-check-circle" style={{ fontSize: "4rem", color: "#22c55e" }}></i>
            <h2 style={{ color: "#283e51", marginTop: "1rem" }}>Registration Successful!</h2>
            <p className="text-muted">
              We've sent a verification OTP to <strong>{emailAddress}</strong>.
              Please check your email and verify your account.
            </p>
          </div>
          <Button
            className="p-button-rounded p-button-primary w-full mt-3"
            label="GO TO LOGIN"
            onClick={goToLogin}
            style={{
              backgroundColor: "#4b79a1",
              border: "none",
              width: "100%",
              fontSize: "1.1rem",
              padding: "0.75rem",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="login-page flex flex-column"
      style={{
        background: "linear-gradient(to right, #4b79a1, #283e51)",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem 0",
      }}
    >
      <div
        className="login-panel card shadow-lg p-4 rounded-lg"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="text-center mb-4">
          <h2 style={{ color: "#283e51" }}>Create Account</h2>
          <p className="text-muted">Join ApolloSMS to start sending messages</p>
        </div>

        <Messages ref={message} style={{ width: "100%" }} />

        <div className="grid">
          <div className="col-12 md:col-6">
            <label htmlFor="firstName" className="block mb-2">First Name</label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user"></i>
              </span>
              <InputText
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <label htmlFor="lastName" className="block mb-2">Last Name</label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-user"></i>
              </span>
              <InputText
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12">
            <label htmlFor="email" className="block mb-2">Email Address</label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-envelope"></i>
              </span>
              <InputText
                id="email"
                type="email"
                value={emailAddress}
                onChange={(e) => setEmailAddress(e.target.value)}
                placeholder="Email address"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <label htmlFor="phoneNumber" className="block mb-2">Phone Number <span className="p-error">*</span></label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-phone"></i>
              </span>
              <InputText
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+256 700 000000"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <label htmlFor="country" className="block mb-2">Country <span className="p-error">*</span></label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-globe"></i>
              </span>
              <Dropdown
                id="country"
                value={countryId}
                options={countries}
                optionValue="id"
                optionLabel="name"
                onChange={(e) => setCountryId(e.value)}
                placeholder="Select your country"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <label htmlFor="password" className="block mb-2">Password <span className="p-error">*</span></label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-lock"></i>
              </span>
              <Password
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                toggleMask
                feedback={true}
                style={{ width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
            </div>
          </div>

          <div className="col-12 md:col-6">
            <label htmlFor="confirmPassword" className="block mb-2">Confirm Password <span className="p-error">*</span></label>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-lock"></i>
              </span>
              <Password
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                toggleMask
                feedback={false}
                style={{ width: "100%" }}
                inputStyle={{ width: "100%" }}
              />
            </div>
          </div>
        </div>

        <Button
          loading={isSaving}
          className="p-button-rounded p-button-primary w-full mb-3 mt-3"
          label="CREATE ACCOUNT"
          onClick={submitRegistration}
          style={{
            backgroundColor: "#4b79a1",
            border: "none",
            width: "100%",
            fontSize: "1.1rem",
            padding: "0.75rem",
          }}
        />

        <div className="text-center mb-3">
          <p className="text-muted">or register with</p>
        </div>

        <div className="social-login-buttons text-center">
          <div className="grid">
            <div className="col-12 md:col-3">
              <Button
                onClick={() => handleOAuthLogin("google")}
                className="p-button-outlined p-button-secondary"
                style={{
                  width: "100%",
                  color: "#db4437",
                  borderColor: "#db4437",
                  fontWeight: "bold",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <FaGoogle /> {getSocialLoginLabel("google")}
                </span>
              </Button>
            </div>
            <div className="col-12 md:col-3">
              <Button
                onClick={() => handleOAuthLogin("facebook")}
                className="p-button-outlined p-button-secondary"
                style={{
                  width: "100%",
                  color: "#3b5998",
                  borderColor: "#3b5998",
                  fontWeight: "bold",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <FaFacebook /> {getSocialLoginLabel("facebook")}
                </span>
              </Button>
            </div>
            <div className="col-12 md:col-3">
              <Button
                onClick={() => handleOAuthLogin("github")}
                className="p-button-outlined p-button-secondary"
                style={{
                  width: "100%",
                  color: "#111827",
                  borderColor: "#111827",
                  fontWeight: "bold",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <FaGithub /> {getSocialLoginLabel("github")}
                </span>
              </Button>
            </div>
            <div className="col-12 md:col-3">
              <Button
                onClick={() => handleOAuthLogin("twitter")}
                className="p-button-outlined p-button-secondary"
                style={{
                  width: "100%",
                  color: "#0f172a",
                  borderColor: "#0f172a",
                  fontWeight: "bold",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                  <FaTwitter /> {getSocialLoginLabel("twitter")}
                </span>
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center mt-3">
          <p className="text-muted">
            Already have an account?{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goToLogin();
              }}
              style={{ color: "#4b79a1", fontWeight: "bold" }}
            >
              Login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
